/**
 * Authentication and the active shop.
 *
 * Tokens live in memory plus `sessionStorage` (not `localStorage`) so closing
 * the tab ends the session — a shared till should not stay logged in.
 *
 * The active shop is part of every React Query key AND is sent on every
 * request, so switching shops can never show another shop's data.
 */
import { create } from 'zustand';
import type { SessionUser } from '@pospak/shared';
import { api, configureApi } from '../lib/api';
import { connectSocket, disconnectSocket } from '../lib/socket';

const STORAGE_KEY = 'pospak.session';

interface StoredSession {
  accessToken: string;
  refreshToken: string;
  shopId: string | null;
}

function readStored(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeStored(session: StoredSession | null): void {
  try {
    if (session) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private-browsing mode — the in-memory session still works for this tab.
  }
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: SessionUser | null;
  activeShopId: string | null;
  status: 'loading' | 'authenticated' | 'anonymous';

  login: (input: {
    businessCode: string;
    username: string;
    password: string;
    totpCode?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  bootstrap: () => Promise<void>;
  setActiveShop: (shopId: string) => void;
  hasPermission: (permission: string) => boolean;
  /** True when the user may write — false on a cloud mirror or a lapsed plan. */
  canWrite: () => boolean;
  /** Forced first-login change or a voluntary one from account settings. */
  changePassword: (input: { currentPassword: string; newPassword: string }) => Promise<void>;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: SessionUser;
}

export const useAuth = create<AuthState>((set, get) => ({
  accessToken: null,
  refreshToken: null,
  user: null,
  activeShopId: null,
  status: 'loading',

  async login(input) {
    const result = await api.post<LoginResponse>('/auth/login', input, { noShop: true });
    const shopId = result.user.activeShopId ?? result.user.shops[0]?.id ?? null;
    writeStored({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      shopId,
    });
    set({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      user: result.user,
      activeShopId: shopId,
      status: 'authenticated',
    });
  },

  async logout() {
    const { refreshToken } = get();
    try {
      await api.post('/auth/logout', { refreshToken }, { noShop: true });
    } catch {
      // Logging out locally matters more than the server acknowledging it.
    }
    writeStored(null);
    set({
      accessToken: null,
      refreshToken: null,
      user: null,
      activeShopId: null,
      status: 'anonymous',
    });
  },

  async bootstrap() {
    const stored = readStored();
    if (!stored) {
      set({ status: 'anonymous' });
      return;
    }
    set({
      accessToken: stored.accessToken,
      refreshToken: stored.refreshToken,
      activeShopId: stored.shopId,
    });
    try {
      const user = await api.get<SessionUser>('/auth/me');
      set({
        user,
        activeShopId: stored.shopId ?? user.activeShopId ?? user.shops[0]?.id ?? null,
        status: 'authenticated',
      });
    } catch {
      writeStored(null);
      set({ accessToken: null, refreshToken: null, user: null, status: 'anonymous' });
    }
  },

  setActiveShop(shopId) {
    const { user, accessToken, refreshToken } = get();
    if (!user?.shops.some((s) => s.id === shopId)) return;
    set({ activeShopId: shopId });
    if (accessToken && refreshToken) writeStored({ accessToken, refreshToken, shopId });
  },

  hasPermission(permission) {
    return get().user?.permissions.includes(permission) ?? false;
  },

  canWrite() {
    const user = get().user;
    return user !== null && !user.cloudReadOnly && !user.subscriptionExpired && user.lockState === 'NONE';
  },

  async changePassword(input) {
    // `changePasswordSchema` (the same schema the API validates the body
    // against) requires `confirmPassword` too, purely as a server-side
    // belt-and-braces re-check — the form's own zod validation already
    // enforced newPassword === confirmPassword before this ever gets
    // called, so mirroring it here is safe and keeps this action's public
    // signature simple (callers only ever have one "new password" value).
    await api.post('/auth/change-password', { ...input, confirmPassword: input.newPassword });
    const user = get().user;
    if (user) set({ user: { ...user, mustChangePassword: false } });
  },
}));

/* -------------------------------------------------------------------------- */
/* Wire the API client to this store (done once, avoids an import cycle)       */
/* -------------------------------------------------------------------------- */

configureApi({
  getTokens: () => {
    const { accessToken, activeShopId } = useAuth.getState();
    return { accessToken, shopId: activeShopId };
  },

  refresh: async () => {
    const { refreshToken, activeShopId } = useAuth.getState();
    if (!refreshToken) return { ok: false, fatal: true };
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        // Fatal only when the server actually rejected the credentials.
        // A 429 (rate limiter), a 5xx, or a 423 (temporarily suspended) means
        // "not now", not "you are signed out" — keep the stored refresh token
        // so the session resumes once the server is willing again.
        const fatal = response.status === 401 || response.status === 403;
        return { ok: false, fatal };
      }
      const { data } = (await response.json()) as { data: LoginResponse };
      writeStored({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        shopId: activeShopId,
      });
      useAuth.setState({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
        status: 'authenticated',
      });
      return { ok: true, accessToken: data.accessToken };
    } catch {
      // Network/DNS/offline — transient by definition.
      return { ok: false, fatal: false };
    }
  },

  onUnauthorized: () => {
    writeStored(null);
    useAuth.setState({
      accessToken: null,
      refreshToken: null,
      user: null,
      status: 'anonymous',
    });
  },
});

/* -------------------------------------------------------------------------- */
/* Realtime — one shared socket, (re)connected whenever the token or the
   active shop changes, torn down on logout. A subscription rather than a
   call in every action (login/bootstrap/setActiveShop/refresh) so nothing
   can forget to keep it in sync.                                          */
/* -------------------------------------------------------------------------- */

useAuth.subscribe((state, prev) => {
  if (state.accessToken && (state.accessToken !== prev.accessToken || state.activeShopId !== prev.activeShopId)) {
    connectSocket(state.accessToken, state.activeShopId);
  } else if (!state.accessToken && prev.accessToken) {
    disconnectSocket();
  }
});
